import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Pause, Play, BellRing, BellOff, Loader2, Plus, Volume2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { filterAlertableFaces, type MemberAlertContext } from '@/lib/alertFilter'
import { FeedSightingCard, type Sighting } from './FeedSightingCard'
import {
  FeedActionDrawer,
  type DrawerFace,
  type DrawerSightingContext,
} from './FeedActionDrawer'
import type { Classification } from '@/types'

interface MemberPermissions {
  canManagePersons: boolean
  canManageClassifications: boolean
}

interface FeedViewProps {
  groupId: string
  isAdmin: boolean
  permissions: MemberPermissions | null
  memberAlertContext: MemberAlertContext
}

const INITIAL_LIMIT = 10
// How often to poll for newly-arrived sightings (replaces the realtime listener).
const POLL_INTERVAL_MS = 4000
const NOTIF_PROMPT_KEY = 'feed_notif_prompt_dismissed'
// Local mute toggle: suppresses feed alerts (browser notification + chime)
// without touching the browser's notification permission, so the receptionist
// can silence the feed and re-enable it instantly.
const NOTIF_MUTED_KEY = 'feed_notif_muted'
// How long a freshly-arrived card keeps its fading highlight ring. Must match
// the `feed-highlight` animation duration in tailwind.config.js.
const HIGHLIGHT_DURATION_MS = 60_000

export function FeedView({
  groupId,
  isAdmin,
  permissions,
  memberAlertContext,
}: FeedViewProps) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState<Sighting[]>([])
  const [buffered, setBuffered] = useState<Sighting[]>([])
  const [classifications, setClassifications] = useState<Classification[]>([])
  const [classMap, setClassMap] = useState<
    Map<string, { color: string; displayName: string }>
  >(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [olderError, setOlderError] = useState<string | null>(null)
  // Whether history remains past what's on screen (backend `hasMore`). Starts optimistic:
  // the initial page comes from listLatestSightings, which doesn't report it.
  const [hasMoreOlder, setHasMoreOlder] = useState(true)
  const [recentlyAddedIds, setRecentlyAddedIds] = useState<Set<string>>(new Set())

  // Notification state
  const supportsNotifications = typeof window !== 'undefined' && 'Notification' in window
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>(
    supportsNotifications ? Notification.permission : 'denied'
  )
  const [notifPromptDismissed, setNotifPromptDismissed] = useState(() => {
    try {
      return localStorage.getItem(NOTIF_PROMPT_KEY) === '1'
    } catch {
      return false
    }
  })
  const [notifMuted, setNotifMuted] = useState(() => {
    try {
      return localStorage.getItem(NOTIF_MUTED_KEY) === '1'
    } catch {
      return false
    }
  })

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerFace, setDrawerFace] = useState<DrawerFace | null>(null)
  const [drawerSighting, setDrawerSighting] = useState<DrawerSightingContext | null>(
    null
  )

  // Track which sighting IDs we've already loaded (visible OR buffered) so the
  // poller doesn't surface them twice.
  const seenIdsRef = useRef<Set<string>>(new Set())

  // Opaque cursor for the next "Load older" page, as handed back by
  // listSightingsForUser. Null until the first history page has been fetched.
  const olderCursorRef = useRef<string | null>(null)
  // Page counter for the same call's page mode, which is what a classification-scoped
  // member gets (their listing goes through face_events and ignores cursors). The feed's
  // initial listLatestSightings page is page 1, so "Load older" starts at 2.
  const olderPageRef = useRef(1)

  // Newest sighting timestamp we've seen — the polling cursor passed as
  // `sinceTimestamp`. Falls back to the feed's mount time so that, when there's
  // no backlog, we only alert on sightings that arrive after the feed opened.
  const newestTimestampRef = useRef<string | null>(null)
  const mountFloorRef = useRef<string>(new Date().toISOString())

  // Keep refs in sync with state so the polling closure sees fresh values
  // without re-subscribing on every render.
  const isPausedRef = useRef(isPaused)
  useEffect(() => {
    isPausedRef.current = isPaused
  }, [isPaused])

  const alertCtxRef = useRef(memberAlertContext)
  useEffect(() => {
    alertCtxRef.current = memberAlertContext
  }, [memberAlertContext])

  // Mirror the mute flag into a ref so the polling closure reads the live
  // value without re-subscribing.
  const notifMutedRef = useRef(notifMuted)
  useEffect(() => {
    notifMutedRef.current = notifMuted
  }, [notifMuted])

  // Lazily-created AudioContext for the notification chime. Browsers block audio
  // until a user gesture, so we (re)create/resume it on user interactions
  // (enabling alerts, toggling mute, the first tap anywhere — see effect below)
  // as well as right before playing.
  const audioCtxRef = useRef<AudioContext | null>(null)
  // Whether the AudioContext is actually `running` (audible). Stays false while
  // the browser keeps it suspended, which drives the "enable sound" nudge below.
  const [audioUnlocked, setAudioUnlocked] = useState(false)
  const ensureAudioContext = useCallback((): AudioContext | null => {
    try {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext
      if (!AC) return null
      if (!audioCtxRef.current) {
        const ctx = new AC()
        audioCtxRef.current = ctx
        // Mirror the real running/suspended state into React so the nudge
        // disappears the moment audio is genuinely unlocked (resume() is async).
        const sync = () => setAudioUnlocked(ctx.state === 'running')
        ctx.addEventListener('statechange', sync)
        sync()
      }
      if (audioCtxRef.current.state === 'suspended') {
        void audioCtxRef.current.resume()
      }
      return audioCtxRef.current
    } catch (err) {
      console.warn('AudioContext unavailable:', err)
      return null
    }
  }, [])

  // Play a short two-note chime via the Web Audio API (no asset needed, works
  // offline). Soft bell-like envelope so it reads as a pleasant alert.
  const playChime = useCallback(() => {
    const ctx = ensureAudioContext()
    if (!ctx) return
    try {
      const now = ctx.currentTime
      // Ascending A5 → E6, the second note staggered slightly after the first.
      const notes = [
        { freq: 880, start: 0 },
        { freq: 1318.5, start: 0.16 },
      ]
      notes.forEach(({ freq, start }) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = freq
        const t0 = now + start
        gain.gain.setValueAtTime(0.0001, t0)
        gain.gain.exponentialRampToValueAtTime(0.3, t0 + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6)
        osc.connect(gain).connect(ctx.destination)
        osc.start(t0)
        osc.stop(t0 + 0.65)
      })
    } catch (err) {
      console.warn('Failed to play chime:', err)
    }
  }, [ensureAudioContext])

  // Unlock audio on the first interaction anywhere on the page. Browsers keep
  // the AudioContext suspended until the page has had a user gesture; a returning
  // user who already granted notifications never sees the "enable alerts" prompt
  // (the only other place we unlock audio), so without this the first chime would
  // play into a suspended context and stay silent. This is why the chime worked
  // locally but not in production: an automated/dev browser disables the autoplay
  // policy, while a real browser enforces it.
  useEffect(() => {
    const unlock = () => {
      ensureAudioContext()
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
      window.removeEventListener('touchstart', unlock)
    }
    window.addEventListener('pointerdown', unlock)
    window.addEventListener('keydown', unlock)
    window.addEventListener('touchstart', unlock)
    return () => {
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
      window.removeEventListener('touchstart', unlock)
    }
  }, [ensureAudioContext])

  // Load classifications once
  useEffect(() => {
    if (!groupId) return
    let cancelled = false
    ;(async () => {
      try {
        const fn = apiCallable('getClassifications')
        const res = await fn({ groupId })
        const data = res.data as { success: boolean; classifications: Classification[] }
        if (!cancelled && data.success) {
          setClassifications(data.classifications)
          const m = new Map<string, { color: string; displayName: string }>()
          data.classifications.forEach((c) =>
            m.set(c.id, { color: c.color, displayName: c.displayName })
          )
          setClassMap(m)
        }
      } catch (err) {
        console.error('Failed to load classifications:', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [groupId])

  const ringNotification = useCallback(
    (s: Sighting) => {
      if (!supportsNotifications) return
      if (Notification.permission !== 'granted') return
      // Muted: keep the browser permission but stay silent — no banner, no chime.
      if (notifMutedRef.current) return

      const matches = filterAlertableFaces(s.faces, alertCtxRef.current)
      if (matches.length === 0) return

      // Audible chime alongside the browser notification.
      playChime()

      const namedMatches = matches.filter((m) => !m.isUnmatched)
      const titleKey =
        namedMatches.length > 0 ? 'feed.notification.titleNamed' : 'feed.notification.titleUnknown'
      const firstNamed = namedMatches[0]
      const title = t(titleKey, {
        count: matches.length,
        // Resolve by the matched face's personId so multiple people sharing a
        // classification don't surface the wrong name.
        name: firstNamed
          ? (s.faces.find((f) => f.personId === firstNamed.personId)?.personName ||
              t('common.unknown'))
          : '',
      })
      const body = t('feed.notification.body', {
        camera: s.cameraName || s.deviceName,
      })
      try {
        const n = new Notification(title, {
          body,
          icon: '/icons/icon-192.png',
          tag: `feed-${s.id}`,
        })
        n.onclick = () => {
          window.focus()
          n.close()
        }
      } catch (err) {
        console.warn('Failed to fire notification:', err)
      }
    },
    [supportsNotifications, t, playChime]
  )

  // Initial load
  const loadInitial = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    setError(null)
    try {
      const fn = apiCallable('listLatestSightings')
      const res = await fn({ groupId, limit: INITIAL_LIMIT })
      const data = res.data as { success: boolean; sightings: Sighting[] }
      if (data.success) {
        // The poller can prepend arrivals while this initial fetch is in flight
        // (both run on mount). Merge by id instead of replacing so we neither
        // drop those arrivals nor render the same sighting twice.
        data.sightings.forEach((s) => seenIdsRef.current.add(s.id))
        // Advance the polling cursor to the newest sighting we just loaded, so
        // the poller only surfaces genuinely newer arrivals from here on.
        data.sightings.forEach((s) => {
          if (!newestTimestampRef.current || s.timestamp > newestTimestampRef.current) {
            newestTimestampRef.current = s.timestamp
          }
        })
        setVisible((prev) => {
          const known = new Set(prev.map((p) => p.id))
          const tail = data.sightings.filter((s) => !known.has(s.id))
          return [...prev, ...tail]
        })
      }
    } catch (err) {
      console.error('Failed to load feed:', err)
      setError(t('feed.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }, [groupId, t])

  useEffect(() => {
    loadInitial()
  }, [loadInitial])

  // Live updates via polling (replaces the Firestore onSnapshot listener).
  // Every POLL_INTERVAL_MS we ask `listLatestSightings` for anything strictly
  // newer than the newest sighting we've seen (`sinceTimestamp`). Because the
  // cursor sits at (or above) the newest backlog item, every result is a genuine
  // new arrival, so we highlight/chime/notify for all of them. `seenIdsRef`
  // dedups against the initial-load race.
  useEffect(() => {
    if (!groupId) return
    let cancelled = false

    const poll = async () => {
      const sinceTimestamp = newestTimestampRef.current || mountFloorRef.current
      try {
        const fn = apiCallable('listLatestSightings')
        const res = await fn({ groupId, sinceTimestamp, limit: INITIAL_LIMIT })
        const data = res.data as { success: boolean; sightings: Sighting[] }
        if (cancelled || !data.success || data.sightings.length === 0) return

        // Keep only sightings we haven't already surfaced.
        const fresh = data.sightings.filter((s) => !seenIdsRef.current.has(s.id))
        // Advance the cursor even if everything was a duplicate, so we don't
        // re-request the same window forever.
        data.sightings.forEach((s) => {
          if (!newestTimestampRef.current || s.timestamp > newestTimestampRef.current) {
            newestTimestampRef.current = s.timestamp
          }
        })
        if (fresh.length === 0) return
        fresh.forEach((s) => seenIdsRef.current.add(s.id))

        if (isPausedRef.current) {
          setBuffered((prev) => [...fresh, ...prev])
        } else {
          setVisible((prev) => {
            const known = new Set(prev.map((p) => p.id))
            const additions = fresh.filter((s) => !known.has(s.id))
            return [...additions, ...prev]
          })
          setRecentlyAddedIds((prev) => {
            const next = new Set(prev)
            fresh.forEach((s) => next.add(s.id))
            return next
          })
          // Keep the "new arrival" flag for the full highlight fade (≈60s),
          // then drop it so the card settles to its resting style.
          setTimeout(() => {
            setRecentlyAddedIds((prev) => {
              const next = new Set(prev)
              fresh.forEach((s) => next.delete(s.id))
              return next
            })
          }, HIGHLIGHT_DURATION_MS)
          fresh.forEach(ringNotification)
        }
      } catch (err) {
        console.error('Failed to poll for new sightings:', err)
      }
    }

    const interval = setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [groupId, ringNotification])

  const handleResume = () => {
    setIsPaused(false)
    setVisible((prev) => [...buffered, ...prev])
    setRecentlyAddedIds((prev) => {
      const next = new Set(prev)
      buffered.forEach((s) => next.add(s.id))
      return next
    })
    // Fire notifications for buffered sightings on resume too — receptionist
    // explicitly asked to see them again.
    buffered.forEach(ringNotification)
    const resumedIds = buffered.map((s) => s.id)
    setTimeout(() => {
      setRecentlyAddedIds((prev) => {
        const next = new Set(prev)
        resumedIds.forEach((id) => next.delete(id))
        return next
      })
    }, HIGHLIGHT_DURATION_MS)
    setBuffered([])
  }

  const handlePause = () => {
    setIsPaused(true)
  }

  const handleLoadOlder = async () => {
    if (visible.length === 0 || loadingOlder) return
    setLoadingOlder(true)
    setOlderError(null)
    try {
      // listLatestSightings takes sinceTimestamp = strictly after, so to load *older* than
      // what's on screen we go through listSightingsForUser, which paginates history.
      //
      // Its `cursor` is an opaque backend token, NOT a sighting id — passing the oldest
      // card's id used to make the backend restart from the newest page, whose sightings we
      // then dropped as duplicates, so the button did nothing. The first click anchors on the
      // oldest visible sighting via `afterSightingId`; every click after that round-trips the
      // `nextCursor` the backend returned.
      const oldest = visible[visible.length - 1]
      const nextPage = olderPageRef.current + 1
      const fn = apiCallable('listSightingsForUser')
      // Send the cursor/anchor AND the page: the backend picks whichever its query path
      // supports (cursor for the standard listing, page for the classification-scoped one).
      const res = await fn({
        groupId,
        period: 'all',
        pageSize: INITIAL_LIMIT,
        page: nextPage,
        ...(olderCursorRef.current
          ? { cursor: olderCursorRef.current }
          : { afterSightingId: oldest.id }),
      })
      const data = res.data as {
        success: boolean
        sightings: Sighting[]
        nextCursor?: string | null
        hasMore?: boolean
        totalCount?: number
      }
      if (data.success) {
        olderPageRef.current = nextPage
        olderCursorRef.current = data.nextCursor || null
        // Which fields come back tells us which mode the backend used.
        setHasMoreOlder(
          typeof data.totalCount === 'number'
            ? nextPage * INITIAL_LIMIT < data.totalCount
            : !!data.hasMore && !!data.nextCursor
        )
        const fresh = data.sightings.filter((s) => !seenIdsRef.current.has(s.id))
        fresh.forEach((s) => seenIdsRef.current.add(s.id))
        setVisible((prev) => [...prev, ...fresh])
      }
    } catch (err) {
      console.error('Failed to load older sightings:', err)
      setOlderError(t('feed.failedToLoadOlder'))
    } finally {
      setLoadingOlder(false)
    }
  }

  const requestNotifPermission = async () => {
    if (!supportsNotifications) return
    // This runs from a click (user gesture) — unlock audio so the first chime
    // can actually play.
    ensureAudioContext()
    try {
      const result = await Notification.requestPermission()
      setNotifPermission(result)
      setNotifPromptDismissed(true)
      try {
        localStorage.setItem(NOTIF_PROMPT_KEY, '1')
      } catch {}
    } catch (err) {
      console.warn('Notification permission request failed:', err)
    }
  }

  const toggleMute = () => {
    // User gesture — unlock/resume audio so unmuting takes effect immediately.
    ensureAudioContext()
    setNotifMuted((prev) => {
      const next = !prev
      try {
        localStorage.setItem(NOTIF_MUTED_KEY, next ? '1' : '0')
      } catch {}
      return next
    })
  }

  const dismissNotifPrompt = () => {
    setNotifPromptDismissed(true)
    try {
      localStorage.setItem(NOTIF_PROMPT_KEY, '1')
    } catch {}
  }

  const handleFaceTap = (
    face: Sighting['faces'][number],
    cropBase64: string | null,
    sighting: Sighting
  ) => {
    setDrawerFace({
      personId: face.personId,
      personName: face.personName,
      classificationId: face.classificationId,
      isUnmatched: face.isUnmatched,
      thumbnailBase64: cropBase64,
    })
    setDrawerSighting({
      sightingId: sighting.id,
      cameraName: sighting.cameraName || sighting.deviceName,
      timestamp: sighting.timestamp,
    })
    setDrawerOpen(true)
  }

  const canManagePersons = isAdmin || !!permissions?.canManagePersons
  const canManageClassifications = isAdmin || !!permissions?.canManageClassifications

  const showNotifPrompt = useMemo(
    () =>
      supportsNotifications &&
      notifPermission === 'default' &&
      !notifPromptDismissed,
    [supportsNotifications, notifPermission, notifPromptDismissed]
  )

  // Alerts are on and unmuted, but the browser hasn't unlocked audio yet — the
  // chime would be silent. Nudge the user to tap once so the AudioContext can
  // resume (the tap itself is the unlocking gesture).
  const showEnableSound =
    supportsNotifications &&
    notifPermission === 'granted' &&
    !notifMuted &&
    memberAlertContext.alertsEnabled &&
    !audioUnlocked

  return (
    <div className="relative">
      {/* Header strip */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-serif text-[28px] font-medium leading-[1.05] tracking-[-0.005em] text-ink">
            {t('feed.title')}
          </h2>
          <hr className="gold-rule mt-3" />
          <p className="mt-3 text-[13px] leading-[1.55] text-ink-soft">
            {t('feed.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {showEnableSound && (
            <button
              type="button"
              onClick={() => ensureAudioContext()}
              title={t('feed.enableSoundHint')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-pill border border-gold bg-gold px-2.5 py-1 text-[12px] font-medium text-paper transition-colors',
                'hover:bg-[#a87c34] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'motion-safe:animate-pulse'
              )}
            >
              <Volume2 className="h-3.5 w-3.5" />
              {t('feed.enableSound')}
            </button>
          )}
          {supportsNotifications && notifPermission === 'granted' && (
            <button
              type="button"
              onClick={toggleMute}
              aria-pressed={notifMuted}
              title={notifMuted ? t('feed.unmute') : t('feed.mute')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-[12px] transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                notifMuted
                  ? 'border-rule text-ink-soft hover:bg-paper-2'
                  : 'border-gold-soft bg-paper-3 text-ink hover:bg-[#fbf3dd]'
              )}
            >
              {notifMuted ? (
                <BellOff className="h-3.5 w-3.5" />
              ) : (
                <BellRing className="h-3.5 w-3.5" />
              )}
              {notifMuted ? t('feed.notificationsMuted') : t('feed.notificationsOn')}
            </button>
          )}
          {supportsNotifications && notifPermission === 'denied' && (
            <span className="inline-flex items-center gap-1.5 rounded-pill border border-rule px-2.5 py-1 text-[12px] text-ink-soft">
              <BellOff className="h-3.5 w-3.5" />
              {t('feed.notificationsBlocked')}
            </span>
          )}
        </div>
      </div>

      {showNotifPrompt && (
        <Card className="mb-4 border-gold-soft bg-paper-3">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2">
              <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <p className="text-[13px] leading-[1.5] text-ink">
                {t('feed.notifPrompt')}
              </p>
            </div>
            <div className="flex gap-2 sm:shrink-0">
              <Button size="sm" variant="outline" onClick={dismissNotifPrompt}>
                {t('feed.notifPromptDismiss')}
              </Button>
              <Button size="sm" onClick={requestNotifPermission}>
                {t('feed.notifPromptEnable')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paused banner */}
      {isPaused && (
        <button
          type="button"
          onClick={handleResume}
          className={cn(
            'sticky top-2 z-30 mb-4 flex w-full items-center justify-between gap-3',
            'rounded-lg border border-gold bg-paper-3 px-4 py-3 shadow-sm transition',
            'hover:bg-[#fbf3dd] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
        >
          <span className="flex items-center gap-2 text-[14px] font-medium text-ink">
            <Pause className="h-4 w-4 text-gold" />
            {buffered.length > 0
              ? t('feed.pausedWithCount', { count: buffered.length })
              : t('feed.paused')}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-pill bg-ink px-3 py-1 text-[12px] font-medium text-paper">
            <Play className="h-3 w-3" />
            {buffered.length > 0 ? t('feed.showNew') : t('feed.resume')}
          </span>
        </button>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Feed */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ink-soft" />
        </div>
      ) : visible.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-[14px] text-ink-soft">
            {t('feed.empty')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((s) => (
            <FeedSightingCard
              key={s.id}
              sighting={s}
              classifications={classMap}
              isNewArrival={recentlyAddedIds.has(s.id)}
              onFaceTap={handleFaceTap}
            />
          ))}
        </div>
      )}

      {visible.length > 0 && (
        <div className="mt-6 flex flex-col items-center gap-2">
          {hasMoreOlder ? (
            <Button
              variant="outline"
              onClick={handleLoadOlder}
              disabled={loadingOlder}
            >
              {loadingOlder ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('feed.loadingOlder')}
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('feed.loadOlder')}
                </>
              )}
            </Button>
          ) : (
            <span className="text-[13px] text-ink-soft">{t('feed.noMoreOlder')}</span>
          )}
          {olderError && (
            <span className="text-[13px] text-destructive">{olderError}</span>
          )}
        </div>
      )}

      {/* Pause / Resume floating control */}
      {!isPaused && (
        <button
          type="button"
          onClick={handlePause}
          className={cn(
            'fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-pill',
            'border border-rule bg-paper-2/95 px-4 py-3 shadow-lg backdrop-blur',
            'hover:bg-paper-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'sm:bottom-6 sm:right-6'
          )}
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          aria-label={t('feed.pause')}
        >
          <Pause className="h-4 w-4 text-ink" />
          <span className="font-sans text-[13px] font-medium text-ink">
            {t('feed.pause')}
          </span>
        </button>
      )}

      <FeedActionDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        groupId={groupId}
        face={drawerFace}
        sighting={drawerSighting}
        classifications={classifications}
        canManagePersons={canManagePersons}
        canManageClassifications={canManageClassifications}
        onActionComplete={() => {
          // No optimistic UI update — the next snapshot will refresh the data
          // naturally as the user keeps watching the feed.
        }}
      />
    </div>
  )
}
